import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils';

/**
 * shadcn-style Tooltip on Radix. Token-driven (base.css), so it follows
 * light/dark automatically. Wrap the app (or a region) in <TooltipProvider>.
 */
export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-[1000] max-w-[260px] overflow-hidden rounded-md bg-text px-2.5 py-1.5 text-xs font-medium leading-[1.45] text-inverse shadow-md animate-fade-in',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * Hint — the one way this app explains a control.
 *
 * Wraps any element (label, button, chip, drag handle) in a Radix tooltip.
 * `asChild` keeps the child's own DOM node as the trigger, so no layout,
 * focus order, or event wiring changes. Keyboard users get it on focus,
 * mouse users on hover; Esc dismisses. Copy lives in `formaTooltips.ts`.
 *
 * Falsy `text` renders the child untouched — so a missing entry degrades to
 * "no tooltip", never to a crash or an empty bubble.
 */
export function Hint({
  text,
  side = 'top',
  children,
}: {
  text?: string | null;
  side?: 'top' | 'right' | 'bottom' | 'left';
  children: React.ReactElement;
}) {
  if (!text) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{text}</TooltipContent>
    </Tooltip>
  );
}
